import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dropdown,
  Option,
  makeStyles,
  tokens,
  Text,
} from '@fluentui/react-components';
import { Building24Regular } from '@fluentui/react-icons';
import { useDevice } from '../../model/DeviceContext';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalXXS,
    paddingTop: 0,
    marginBottom: tokens.spacingVerticalS,
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    fontWeight: tokens.fontWeightSemibold,
  },
  dropdown: {
    minWidth: '100%',
    '& .fui-Option': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    '& .fui-Option[aria-selected="true"]': {
      backgroundColor: tokens.colorPaletteGreenBackground2,
      fontWeight: tokens.fontWeightSemibold,
    },
  },
  singleCompanyDisplay: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  companyName: {
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
});

interface CompanySelectorProps {
  selectedCompanyId?: string | null;
  onCompanyChange?: (companyId: string | null) => void;
}

const CompanySelector: React.FC<CompanySelectorProps> = ({ selectedCompanyId, onCompanyChange }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const { subscription } = useDevice();

  // Auto-select first company if none selected and companies available
  useEffect(() => {
    if (!selectedCompanyId && subscription?.companies && subscription.companies.length > 0) {
      const firstCompanyId = subscription.companies[0].companyId;
      console.log('🏢 Auto-selecting first company:', firstCompanyId);

      // Notify parent to update selectedCompanyId
      if (onCompanyChange) {
        onCompanyChange(firstCompanyId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription?.companies, selectedCompanyId]); // Removed onCompanyChange to prevent infinite loops

  const handleCompanyChange = (event: any, data: any) => {
    const companyId = data.optionValue as string;
    onCompanyChange?.(companyId); // Parent (App.tsx) will update selectedCompanyId and localStorage
  };

  if (!subscription?.companies || subscription.companies.length === 0) {
    return null;
  }

  const isSingleCompany = subscription.companies.length === 1;
  const selectedCompany = subscription.companies.find(c => c.companyId === selectedCompanyId);

  // Sort companies alphabetically by name
  const sortedCompanies = [...subscription.companies].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  return (
    <div className={styles.container}>
      <label className={styles.label}>
        <Building24Regular />
        <Text>
          {isSingleCompany
            ? t('company_selector.label_single', 'Podmiot BDO')
            : t('company_selector.label', 'Wybierz podmiot BDO')
          }
        </Text>
      </label>

      {isSingleCompany ? (
        <div className={styles.singleCompanyDisplay}>
          <Text className={styles.companyName}>
            {subscription.companies[0].name}
          </Text>
        </div>
      ) : (
        <Dropdown
          className={styles.dropdown}
          placeholder={t('company_selector.placeholder', 'Wybierz podmiot BDO...')}
          value={selectedCompany?.name || ''}
          selectedOptions={selectedCompanyId ? [selectedCompanyId] : []}
          onOptionSelect={handleCompanyChange}
        >
          {sortedCompanies.map((company) => (
            <Option key={company.companyId} value={company.companyId}>
              {company.name}
            </Option>
          ))}
        </Dropdown>
      )}
    </div>
  );
};

export default CompanySelector;
